// src/pages/AddPropertyPage.js
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createProperty, updateProperty } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import ImageUploader from "../components/ImageUploader";
import { LOCATIONS, PROPERTY_TYPES, FURNISHING, FACING } from "../utils/constants";
import toast from "react-hot-toast";

const INPUT = { width: "100%", padding: "10px 14px", border: "1.5px solid #e2ece2", borderRadius: 8, fontFamily: "inherit", fontSize: 14, color: "#1a2e1a", background: "white" };
const LABEL = { fontSize: 13, fontWeight: 600, color: "#1a2e1a", display: "block", marginBottom: 5 };

export default function AddPropertyPage({ editData = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title:       editData?.title       || "",
    type:        editData?.type        || "House",
    listingType: editData?.listingType || "Sale",
    price:       editData?.price       || "",
    location:    editData?.location    || "Civil Lines",
    area:        editData?.area        || "",
    bedrooms:    editData?.bedrooms    || "",
    bathrooms:   editData?.bathrooms   || "",
    floor:       editData?.floor       || "",
    facing:      editData?.facing      || "",
    furnishing:  editData?.furnishing  || "",
    contact:     editData?.contact     || "",
    description: editData?.description || "",
    ownerOnly:   editData?.ownerOnly   || false,
    urgent:      editData?.urgent      || false,
  });

  const [images, setImages] = useState(editData?.images || []);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.title.trim())   { toast.error("Property title is required"); return false; }
    if (!form.price)          { toast.error("Price is required"); return false; }
    if (!form.contact || form.contact.length !== 10) { toast.error("Enter a valid 10-digit mobile number"); return false; }
    if (!form.area)           { toast.error("Area is required"); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const data = {
        ...form,
        price:     Number(form.price),
        area:      Number(form.area),
        bedrooms:  form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        images,
      };
      if (editData?.id) {
        await updateProperty(editData.id, data);
        toast.success("Property updated!");
        navigate("/dashboard");
      } else {
        await createProperty(data, user.uid);
        setSubmitted(true);
      }
    } catch (e) {
      toast.error("Submission failed: " + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <div style={{ maxWidth: 480, margin: "60px auto", padding: "0 16px", textAlign: "center" }}>
      <div style={{ background: "white", borderRadius: 16, padding: 40, border: "1px solid #e2ece2" }}>
        <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
        <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 22, marginBottom: 8, color: "#1a2e1a" }}>
          Property Submitted!
        </h2>
        <p style={{ color: "#6b7c6b", fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
          Your listing has been submitted successfully.<br />
          Our team will verify and publish it within <b>24 hours</b>.<br />
          You'll be able to track it from your dashboard.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={() => navigate("/dashboard")} style={{ padding: "12px", borderRadius: 10, background: "#1a6b3c", color: "white", border: "none", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
            Go to Dashboard
          </button>
          <button onClick={() => { setSubmitted(false); setForm({ title:"",type:"House",listingType:"Sale",price:"",location:"Civil Lines",area:"",bedrooms:"",bathrooms:"",floor:"",facing:"",furnishing:"",contact:"",description:"",ownerOnly:false,urgent:false }); setImages([]); }} style={{ padding: "11px", borderRadius: 10, background: "white", color: "#1a6b3c", border: "1.5px solid #1a6b3c", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Post Another Property
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "20px 16px 40px" }}>
      <h1 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 22, color: "#1a2e1a", marginBottom: 4 }}>
        {editData ? "Edit Property" : "Post Your Property"}
      </h1>
      <p style={{ color: "#6b7c6b", fontSize: 14, marginBottom: 24 }}>
        {editData ? "Update your property details below." : "List your property FREE on apnasatna.com — reach buyers across Satna."}
      </p>

      <div style={{ background: "white", borderRadius: 16, padding: 20, border: "1px solid #e2ece2", display: "flex", flexDirection: "column", gap: 0 }}>

        {/* ── Listing Type Toggle ── */}
        <Section title="Listing Type">
          <div style={{ display: "flex", gap: 8 }}>
            {["Sale", "Rent"].map(t => (
              <button key={t} onClick={() => set("listingType", t)} style={{
                flex: 1, padding: "10px", borderRadius: 8, fontWeight: 700, fontSize: 14,
                border: `2px solid ${form.listingType === t ? "#1a6b3c" : "#e2ece2"}`,
                background: form.listingType === t ? "#f0fff4" : "white",
                color: form.listingType === t ? "#1a6b3c" : "#6b7c6b",
                cursor: "pointer", fontFamily: "inherit"
              }}>{t === "Sale" ? "🏠 For Sale" : "🔑 For Rent"}</button>
            ))}
          </div>
        </Section>

        {/* ── Basic Info ── */}
        <Section title="Basic Information">
          <Field label="Property Title *">
            <input value={form.title} onChange={e => set("title", e.target.value)}
              placeholder="e.g. 3BHK Independent House near Civil Lines" style={INPUT} />
          </Field>
          <Grid2>
            <Field label="Property Type *">
              <select value={form.type} onChange={e => set("type", e.target.value)} style={INPUT}>
                {PROPERTY_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Location *">
              <select value={form.location} onChange={e => set("location", e.target.value)} style={INPUT}>
                {LOCATIONS.map(l => <option key={l}>{l}</option>)}
              </select>
            </Field>
          </Grid2>
        </Section>

        {/* ── Price & Area ── */}
        <Section title="Price & Area">
          <Grid2>
            <Field label={`${form.listingType === "Rent" ? "Monthly Rent" : "Sale Price"} (₹) *`}>
              <input value={form.price} onChange={e => set("price", e.target.value)}
                type="number" placeholder={form.listingType === "Rent" ? "e.g. 8000" : "e.g. 2500000"} style={INPUT} />
            </Field>
            <Field label="Area (sq.ft) *">
              <input value={form.area} onChange={e => set("area", e.target.value)}
                type="number" placeholder="e.g. 1200" style={INPUT} />
            </Field>
          </Grid2>
        </Section>

        {/* ── Property Details ── */}
        <Section title="Property Details">
          <Grid2>
            {(form.type === "House" || form.type === "Flat") && <>
              <Field label="Bedrooms (BHK)">
                <select value={form.bedrooms} onChange={e => set("bedrooms", e.target.value)} style={INPUT}>
                  <option value="">Select</option>
                  {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} BHK</option>)}
                </select>
              </Field>
              <Field label="Bathrooms">
                <select value={form.bathrooms} onChange={e => set("bathrooms", e.target.value)} style={INPUT}>
                  <option value="">Select</option>
                  {[1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
            </>}
            <Field label="Facing">
              <select value={form.facing} onChange={e => set("facing", e.target.value)} style={INPUT}>
                <option value="">Select</option>
                {FACING.map(f => <option key={f}>{f}</option>)}
              </select>
            </Field>
            {(form.type === "House" || form.type === "Flat") && (
              <Field label="Furnishing">
                <select value={form.furnishing} onChange={e => set("furnishing", e.target.value)} style={INPUT}>
                  <option value="">Select</option>
                  {FURNISHING.map(f => <option key={f}>{f}</option>)}
                </select>
              </Field>
            )}
            {form.type === "Flat" && (
              <Field label="Floor No.">
                <input value={form.floor} onChange={e => set("floor", e.target.value)} placeholder="e.g. 3rd" style={INPUT} />
              </Field>
            )}
          </Grid2>
        </Section>

        {/* ── Description ── */}
        <Section title="Description">
          <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={4}
            placeholder="Describe your property — nearby landmarks, amenities, road access, condition..."
            style={{ ...INPUT, resize: "vertical" }} />
        </Section>

        {/* ── Images ── */}
        <Section title="Property Photos">
          <ImageUploader images={images} setImages={setImages} maxImages={6} />
          <p style={{ fontSize: 12, color: "#6b7c6b", marginTop: 8 }}>
            First image will be the cover photo. Up to 6 images, max 5MB each.
          </p>
        </Section>

        {/* ── Contact ── */}
        <Section title="Contact Information">
          <Field label="Mobile Number *">
            <input value={form.contact} onChange={e => set("contact", e.target.value.replace(/\D/g, ""))}
              type="tel" placeholder="10-digit mobile number" maxLength={10} style={INPUT} />
          </Field>
          <p style={{ fontSize: 12, color: "#6b7c6b", marginTop: 4 }}>
            Buyers will contact you directly via call and WhatsApp on this number.
          </p>
        </Section>

        {/* ── Tags ── */}
        <Section title="Special Tags">
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <CheckTag
              checked={form.ownerOnly} onChange={v => set("ownerOnly", v)}
              bg="#fff3e0" color="#e65100" emoji="👤" label="Owner Only (No Broker)"
              desc="You are the direct owner — no broker involved"
            />
            <CheckTag
              checked={form.urgent} onChange={v => set("urgent", v)}
              bg="#fde8e8" color="#c0392b" emoji="🔥" label="Urgent Sale"
              desc="Highlight this listing as urgent — attracts more buyers"
            />
          </div>
        </Section>

        {/* ── Info strip ── */}
        <div style={{ background: "#f0fff4", border: "1px solid #c3e6cb", borderRadius: 10, padding: "12px 14px", marginBottom: 20, fontSize: 13, color: "#1a6b3c" }}>
          ✓ Free listing on ApnaSatna.com &nbsp;·&nbsp; ✓ WhatsApp contact button &nbsp;·&nbsp; ✓ Verified badge after review
        </div>

        {/* ── Submit ── */}
        <button onClick={handleSubmit} disabled={submitting} style={{
          width: "100%", padding: "15px", borderRadius: 10,
          background: submitting ? "#a8d5b9" : "#1a6b3c", color: "white",
          border: "none", fontWeight: 700, fontSize: 16, cursor: submitting ? "not-allowed" : "pointer",
          fontFamily: "inherit"
        }}>
          {submitting ? "Submitting..." : editData ? "Update Property" : "Submit Property Listing →"}
        </button>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ borderBottom: "1px solid #f0f4f0", paddingBottom: 18, marginBottom: 18 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: "#1a6b3c", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</h3>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={LABEL}>{label}</label>
      {children}
    </div>
  );
}
function Grid2({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
}
function CheckTag({ checked, onChange, bg, color, emoji, label, desc }) {
  return (
    <label style={{ flex: "1 1 200px", display: "flex", alignItems: "flex-start", gap: 10, background: checked ? bg : "#fafafa", border: `1.5px solid ${checked ? color : "#e2ece2"}`, borderRadius: 10, padding: "12px 14px", cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ accentColor: color, width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
      <div>
        <p style={{ fontWeight: 700, fontSize: 13, color: checked ? color : "#1a2e1a" }}>{emoji} {label}</p>
        <p style={{ fontSize: 12, color: "#6b7c6b", marginTop: 2 }}>{desc}</p>
      </div>
    </label>
  );
}
